'use client';

import './calendar.scss';

import { ComponentProps, FC, useEffect, useRef } from 'react';
import {
  DateLib,
  DayButton,
  DayPicker,
  DayPickerProps,
  Dropdown,
  Locale,
} from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';

import { ButtonStyle } from '../../atoms/button-style/button-style';
import { cn } from '../../utils/helpers';
import { Select } from '../select/select';

export interface CalendarProps {
  className?: string;
  showOutsideDays?: boolean;
}

const getTranslationKey = (key: string): string => {
  return `core.calendar.${key}`;
};

const CalendarDayButton: FC<ComponentProps<typeof DayButton>> = ({
  day,
  modifiers,
  ...props
}) => {
  const {
    selected,
    focused,
    disabled,
    range_start: rangeStart,
    range_end: rangeEnd,
    range_middle: rangeMiddle,
  } = modifiers;
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (focused) {
      ref.current?.focus();
    }
  }, [focused]);

  return (
    <ButtonStyle
      size="md"
      variant={selected ? 'primary' : 'tertiary'}
      purpose={selected ? 'positive' : 'neutral'}
      disabled={disabled}
      className="Calendar__day-button label-md"
    >
      <button
        ref={ref}
        type="button"
        data-day={day.date.toLocaleDateString()}
        data-selected-single={
          selected && !rangeStart && !rangeEnd && !rangeMiddle
        }
        data-range-start={rangeStart}
        data-range-end={rangeEnd}
        data-range-middle={rangeMiddle}
        {...props}
      />
    </ButtonStyle>
  );
};

const CalendarSelect: FC<
  { name: string } & ComponentProps<typeof Dropdown>
> = ({ name, options, ...props }) => {
  const selectOptions = options ?? [];

  return (
    <Select
      name={name}
      fieldClasses="mx-snug-lg"
      selectClasses="Calendar__select"
      items={selectOptions
        ?.filter((option) => !option.disabled)
        .map(({ value, label }) => {
          return {
            value: value.toString(),
            text: label,
          };
        })}
      {...props}
    />
  );
};

export const Calendar: FC<DayPickerProps> = ({
  className,
  formatters,
  locale: calendarLocal,
  showOutsideDays = true,
  captionLayout = 'dropdown',
  autoFocus = true,
  ...props
}) => {
  const { t } = useTranslation();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-comfy-md', className)}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (month) => {
          const dateLib = new DateLib({ locale: calendarLocal as Locale });
          return dateLib.format(month, 'LLL');
        },
        ...formatters,
      }}
      labels={{
        labelNext: () => t(getTranslationKey('nextButtonAriaLabel')),
        labelPrevious: () => t(getTranslationKey('previousButtonAriaLabel')),
        labelMonthDropdown: () => t(getTranslationKey('monthSelectAriaLabel')),
        labelYearDropdown: () => t(getTranslationKey('yearSelectAriaLabel')),
      }}
      classNames={{
        months: 'flex flex-col md:flex-row relative',
        month: 'flex flex-col w-full gap-4',
        nav: 'flex absolute w-full justify-between',
        button_previous: 'Calendar__nav-button flex align-center p-snug-xl',
        button_next: 'Calendar__nav-button flex align-center p-snug-xl',
        month_caption: 'Calendar__month flex items-center justify-center',
        month_grid: 'w-full border-collapse',
        dropdowns: 'flex items-center justify-center',
        weekdays: 'flex mb-snug-lg mt-comfy-lg',
        weekday: 'Calendar__weekday flex-1 label-md label-subtle p-snug-xs',
        week: 'flex w-full mt-2',
        day: 'Calendar__day relative w-full text-center p-snug-xs',
        day_button: 'w-full',
        today: cn('Calendar__day--today'),
        selected: cn('Calendar__day--selected'),
        outside: cn('Calendar__day--outside'),
        disabled: cn('Calendar__day--disabled'),
      }}
      components={{
        YearsDropdown: (dropdownProps) => (
          <CalendarSelect name="years" {...dropdownProps} />
        ),
        MonthsDropdown: (dropdownProps) => (
          <CalendarSelect name="months" {...dropdownProps} />
        ),
        Chevron: ({ orientation }) => (
          <ArkIcon iconName={`chevron-${orientation ?? 'down'}`} size="1.5em" />
        ),
        DayButton: (buttonProps) => <CalendarDayButton {...buttonProps} />,
      }}
      autoFocus={autoFocus}
      {...props}
    />
  );
};
